import { Icon } from '../../components/Icon'
import type { LocalChannel } from '../../db/db'
import { channelDisplayName } from '../../lib/channels'
import { useConversation } from './ConversationContext'
import { saveNotify } from '../channels/notify'
import { defaultNotify, quietLabel, quietState } from '../channels/quiet'
import { toast } from '../../state/toasts'

/**
 * What this conversation will not do, on the conversation itself: amber, because it is something the person did to
 * their own notifications, and the commonest reason to read it is having done it by mistake. So the way back is here
 * rather than two screens away: the state opens the settings, the button next to it acts, and the toast offers the
 * mute back exactly as it was.
 */
export function QuietChip({ channel, onOpenDetails }: { channel: LocalChannel; onOpenDetails: (section: 'notifications') => void }) {
  const { users, me } = useConversation()
  const quiet = quietState(channel)
  const label = quietLabel(quiet, 'short')
  if (!quiet || !label) return null
  const name = channelDisplayName(channel, users, me.id)
  const off = quiet.kind === 'off'

  const restore = async () => {
    const previous = { notify: channel.notify, mutedUntil: channel.mutedUntil }
    const saved = await saveNotify(channel, off ? defaultNotify(channel) : channel.notify, null)
    if (!saved) return
    toast(off ? `Notifications on for ${name}` : `Unmuted ${name}`, {
      action: { label: 'Undo', onClick: () => void saveNotify(channel, previous.notify, previous.mutedUntil) },
    })
  }

  return (
    <span className="quiet-chip">
      <button type="button" className="quiet-state" aria-label={`${label}. Notification settings`} onClick={() => onOpenDetails('notifications')}>
        <Icon name="mute" />
        <span>{label}</span>
      </button>
      <button type="button" className="quiet-undo" onClick={() => void restore()}>
        {off ? 'Turn on' : 'Unmute'}
      </button>
    </span>
  )
}
